import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTASection } from "@/pages/Home";
import useSEO from "@/hooks/useSEO";
import { IMAGES } from "@/data/content";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "Who We Are",
    text: "SSDE LTD is a UK-based creative company providing specialised design services for individuals, start-ups and established businesses. We believe strong design is quiet, deliberate and precise — work that earns trust before a single word is read.",
  },
  {
    id: "what-we-do",
    title: "What We Do",
    text: "SSDE LTD provides specialised design services across graphic design, branding, visual identity, marketing materials, packaging, website layouts, user interface design, and digital content creation. Our aim is to help clients communicate their ideas clearly, develop a distinctive visual identity, and strengthen their brand presence through creative and professional design solutions.",
  },
  {
    id: "our-approach",
    title: "Our Approach",
    text: "Every project begins with listening. We take time to understand your goals, your audience and the character of your organisation before a single concept is drawn. From there, ideas are explored openly, refined carefully, and delivered with the polish your brand deserves.",
  },
];

const VALUES = [
  {
    title: "Clarity",
    text: "Design should communicate, not decorate. We prioritise clear hierarchy, legible typography and layouts that guide the eye naturally.",
  },
  {
    title: "Creativity",
    text: "Original thinking sits at the heart of every brief. We explore widely, then distil ideas into work that feels distinctive and right.",
  },
  {
    title: "Consistency",
    text: "A brand is built through repetition. We design systems — colour, type, imagery — that hold together across every touchpoint.",
  },
  {
    title: "Care",
    text: "Details matter. From print finishes to pixel alignment, we treat every element of your project with the attention it deserves.",
  },
];

const About = () => {
  useSEO(
    "About Us — SSDE LTD | Design with Purpose",
    "Learn about SSDE LTD, a UK creative design agency providing specialised design services across branding, graphic design, packaging, web layouts and digital content."
  );
  return (
    <>
      <PageHero
        testId="about-hero"
        eyebrow="About SSDE LTD"
        titleLines={["Design with", "Purpose."]}
        lede="A creative design practice built on the belief that thoughtful, well-crafted design helps businesses communicate with clarity and confidence."
      />

      <section className="bg-white py-20 sm:py-28" data-testid="about-story">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <figure className="relative">
                <img
                  src={IMAGES.aboutStudio}
                  alt="Design studio bookshelf with art books and creative references"
                  className="w-full aspect-[4/5] object-cover border border-line"
                  loading="lazy"
                  data-testid="about-image-studio"
                />
                <figcaption className="absolute -bottom-5 -right-3 sm:-right-5 bg-white border border-line px-5 py-3 shadow-[0_16px_44px_-20px_rgba(37,40,45,0.25)]">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold-deep">
                    Est. in Ilford, UK
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-14">
            {SECTIONS.map((s, i) => (
              <Reveal key={s.id} delay={0.05 * i}>
                <div data-testid={`about-section-${s.id}`}>
                  <div className="flex items-center gap-4 mb-5">
                    <span className="font-mono text-[11px] tracking-[0.2em] text-gold-deep">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="w-10 h-px bg-gold" aria-hidden="true" />
                    <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-charcoal">{s.title}</h2>
                  </div>
                  <p className="text-base text-charcoal-mute leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory border-y border-line py-20 sm:py-28" data-testid="about-principles">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mb-14">
            <Reveal className="lg:col-span-5">
              <p className="eyebrow mb-4">How We Think</p>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight text-charcoal leading-[1.15]">
                Our Design Principles
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-7">
              <img
                src={IMAGES.aboutEditorial}
                alt="Open editorial magazine with refined typographic layout"
                className="w-full aspect-[16/8] object-cover border border-line"
                loading="lazy"
                data-testid="about-image-editorial"
              />
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="h-full bg-white p-8" data-testid={`about-value-${v.title.toLowerCase()}`}>
                  <span className="block w-8 h-[3px] bg-gold mb-6" aria-hidden="true" />
                  <h3 className="font-serif text-xl text-charcoal">{v.title}</h3>
                  <p className="mt-3 text-sm text-charcoal-mute leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default About;
